import { ProcessSkillOption } from '@/api/processes-api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Save } from 'lucide-react';

interface Work4MeAgentFormProps {
  name: string;
  description: string;
  instructions: string;
  isEnabled: boolean;
  selectedSkillIds: Set<number>;
  skillOptions: ProcessSkillOption[];
  isDirty: boolean;
  isSaving: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onInstructionsChange: (value: string) => void;
  onIsEnabledChange: (value: boolean) => void;
  onSkillToggle: (skillId: number, checked: boolean) => void;
  onSave: () => void;
  submitLabel?: string;
  submittingLabel?: string;
  editableHint?: string;
}

export function Work4MeAgentForm({
  name,
  description,
  instructions,
  isEnabled,
  selectedSkillIds,
  skillOptions,
  isDirty,
  isSaving,
  onNameChange,
  onDescriptionChange,
  onInstructionsChange,
  onIsEnabledChange,
  onSkillToggle,
  onSave,
  submitLabel = 'Save Agent',
  submittingLabel = 'Saving...',
  editableHint = 'Changes are applied immediately when saved.',
}: Work4MeAgentFormProps) {
  const canSave = name.trim().length > 0 && instructions.trim().length > 0 && !isSaving;

  return (
    <>
      <div className="grid gap-4 px-4 pt-4">
        <div>
          <Label htmlFor="agent-name">Name</Label>
          <Input
            id="agent-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="agent-description">Description</Label>
          <Textarea
            id="agent-description"
            rows={3}
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            className="mt-1 resize-none"
          />
        </div>

        <div>
          <Label htmlFor="agent-instructions">Instructions</Label>
          <Textarea
            id="agent-instructions"
            rows={10}
            value={instructions}
            onChange={(event) => onInstructionsChange(event.target.value)}
            className="mt-1"
          />
        </div>

        <div className="space-y-2">
          <Label>Linked skills</Label>
          <div className="max-h-44 overflow-y-auto rounded border p-3 space-y-2">
            {skillOptions.map((skill) => (
              <label key={skill.id} className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selectedSkillIds.has(skill.id)}
                  onCheckedChange={(checked) => onSkillToggle(skill.id, checked === true)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">{skill.name}</span>
                  {skill.description ? (
                    <span className="block text-xs text-muted-foreground">{skill.description}</span>
                  ) : null}
                </span>
              </label>
            ))}
            {skillOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground">No selectable skills found in this workspace.</p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between rounded border p-3">
          <div>
            <p className="text-sm font-medium">Enabled</p>
            <p className="text-xs text-muted-foreground">Disabled agents stay stored but cannot run.</p>
          </div>
          <Switch checked={isEnabled} onCheckedChange={onIsEnabledChange} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
        <p className="text-xs text-muted-foreground">{editableHint}</p>
        <Button
          onClick={onSave}
          className="gap-2"
          disabled={!canSave || !isDirty}
        >
          <Save className="h-4 w-4" />
          {isSaving ? submittingLabel : submitLabel}
        </Button>
      </div>
    </>
  );
}
